import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { Provider } from 'react-redux';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { store } from './model/store';
import { queryClient } from '@client/api/queryClient';
import { BrowserRouter } from 'react-router-dom';
import { Router } from './Router';
import './styles/globals.css';
import './main.css';
import { addLogSender, isRunningLocal, isRunningUnitTest } from '@shared';
import { clientLogSender } from './util';
import { logRocketInit } from '@shared';
import ScrollToTop from './components/ScrollToTop';
import { IntercomUpdate } from './components';
import { Toaster } from './components/ui/toaster';
import { Toaster as SonnerToaster } from '@design-system';
import { OnboardingTimeTracker } from './components/OnboardingTimeTracker';
import { AppOpenTracker } from './components/AppOpenTracker';
import { FeatureFlagsProvider } from './contexts/FeatureFlagsContext';
import { ClerkAuthProvider } from './components/ClerkAuthProvider';
import { ConnectionBanner } from './components/ConnectionBanner';
import { PostHogWrapper } from './components/PosthogProvider';

const VITE_SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
if (!isRunningLocal()) {
  Sentry.init({
    dsn: VITE_SENTRY_DSN,
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    integrations: [
      Sentry.replayIntegration({
        maskAllText: import.meta.env.VITE_STAGE !== 'dev',
        blockAllMedia: import.meta.env.VITE_STAGE !== 'dev',
      }),
      Sentry.consoleLoggingIntegration({ levels: ['log', 'warn', 'error'] }),
    ],
    // Session Replay
    replaysSessionSampleRate: import.meta.env.VITE_STAGE === 'dev' ? 1.0 : 0.5, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    // Enable logs to be sent to Sentry
    enableLogs: true,
  });
}

logRocketInit();
addLogSender(clientLogSender);

if (!isRunningUnitTest()) {
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <PostHogWrapper>
        <BrowserRouter>
          <ClerkAuthProvider>
            <Provider store={store}>
              <QueryClientProvider client={queryClient}>
                <FeatureFlagsProvider>
                  <ConnectionBanner />
                  <ScrollToTop />
                  <IntercomUpdate />
                  <AppOpenTracker />
                  <Router />
                  <OnboardingTimeTracker />
                </FeatureFlagsProvider>
                <ReactQueryDevtools initialIsOpen={false} />
              </QueryClientProvider>
            </Provider>
          </ClerkAuthProvider>
        </BrowserRouter>
        <Toaster />
        <SonnerToaster
          position="top-right"
          richColors
        />
      </PostHogWrapper>
    </React.StrictMode>,
  );
}
